'use client';

import { CampaignsInfo, CardCalendarInfo } from '../types';
import Card from '../card/Card';
import fetcher, { Response } from '@/lib/utils/fetcherBasic';
import { AppConfig } from '@/utils/AppConfig';
import { useEffect, useState } from 'react';
import { EdfCampaigns } from '@/lib/directus';
import { LiveData } from '@/lib/repositories';
import mapCampaignToScoreCards from '@/lib/mappers/mapCampaignToScoreCards';

interface Props {
  data: CampaignsInfo;
}

const getDataLive = async () => {
  const live = await fetcher<Array<LiveData>>(`/api/live`, {
    cache: 'no-store',
  });

  return {
    live,
  };
};

export default function ScoreCardsWithFiltersBlock({ data }: Props) {
  const [calendarInfoCards, setCalendarInfoCards] = useState<Array<CardCalendarInfo>>([]);
  const [currentLives, setCurrentLives] = useState<Array<string>>([]);
  const [teamParams, setTeamParams] = useState<{
    teamName: string;
    year: number;
    competition: number;
  }>({
    teamName: data.defaultFilter.teamName || data.teamName,
    competition: data.defaultFilter.competition,
    year: data.defaultFilter.year,
  });

  const handleSelectTeamChanged = (teamName: string) => {
    const teamCompetitions = data.competitionsGroupedByTeam[teamName];

    if (!teamCompetitions) {
      setCalendarInfoCards([]);
      setTeamParams((prevState) => {
        return { ...prevState, teamName };
      });
      return;
    }

    const competitionId = Object.keys(teamCompetitions)[0];
    const year = teamCompetitions[competitionId][0];
    setTeamParams((prevState) => {
      return { ...prevState, teamName, competition: parseInt(competitionId), year };
    });
  };

  const handleSelectCompetitionChanged = (competitionId: string) => {
    const year = data.competitionsGroupedByTeam[teamParams.teamName][competitionId][0];
    setTeamParams((prevState) => {
      return { ...prevState, competition: parseInt(competitionId), year };
    });
  };

  const handleSelectCompetitionYearChange = (year: string) => {
    setTeamParams((prevState) => {
      return { ...prevState, year: parseInt(year) };
    });
  };

  const fetchCampaignComposition = async (teamName: string, competition: number, year: number) => {
    const queryParams = `?team=${teamName}&competition=${competition}&year=${year}`;
    const response = await fetcher<Array<EdfCampaigns>>(
      `${AppConfig.websiteBasePublicUrl}/api/campaign/scores${queryParams}`,
    );

    if ((response as Response).message === 'error') {
    } else {
      const campaigns = response as Array<EdfCampaigns>;

      if (campaigns.length > 0) {
        const scoreCards = await mapCampaignToScoreCards(campaigns[0].matches);

        setCalendarInfoCards(scoreCards);
      }
    }
  };

  useEffect(() => {
    const fetchDataLive = async () => {
      const data = await getDataLive();
      let lives = data?.live as Array<LiveData>;

      if (!lives || !Array.isArray(lives)) {
        return;
      }

      const liveMatchIds = lives
        .filter((live) => live.matchStatus === 'IN_PROGRESS')
        .map((live) => live.matchId);
      setCurrentLives(liveMatchIds);
    };

    fetchDataLive();
  }, []);

  useEffect(() => {
    fetchCampaignComposition(teamParams.teamName, teamParams.competition, teamParams.year);
  }, [teamParams.teamName, teamParams.competition, teamParams.year]);

  const teamNames = data.teamsDetails.map((team) => team.name || '');
  const teamCompetitionTypes = data.competitionsGroupedByTeam[teamParams.teamName]
    ? Object.keys(data.competitionsGroupedByTeam[teamParams.teamName])
    : [];
  const teamCompetitionYears = data.competitionsGroupedByTeam[teamParams.teamName]
    ? data.competitionsGroupedByTeam[teamParams.teamName][teamParams.competition]
    : [];

  return (
    <div className="lg:bg-horizontal-gradient-block-bar mb-[40px] min-h-[800px] bg-stone-50 px-2 py-10 bg-bar-hero-horizontal-gradient lg:mb-[70px] lg:p-10">
      <div className="mb-8 flex gap-2.5">
        {teamNames.length > 1 && (
          <select
            className="select select-bordered max-w-xs rounded-[100px] text-sm font-bold focus:outline-none"
            value={teamParams.teamName}
            onChange={(e) => handleSelectTeamChanged(e.target.value)}
          >
            {teamNames.map((teamName, index) => {
              return (
                <option key={index} value={teamName}>
                  {teamName}
                </option>
              );
            })}
          </select>
        )}
        {teamCompetitionTypes.length > 0 && (
          <select
            className="select select-bordered max-w-xs rounded-[100px] text-sm font-bold focus:outline-none"
            value={teamParams.competition}
            onChange={(e) => handleSelectCompetitionChanged(e.target.value)}
          >
            {teamCompetitionTypes.map((competitionId, index) => {
              return (
                <option key={index} value={competitionId}>
                  {data.competitionNamesById[competitionId]}
                </option>
              );
            })}
          </select>
        )}
        {teamCompetitionYears && teamCompetitionYears.length > 0 && (
          <select
            className="select select-bordered max-w-xs rounded-[100px] text-sm font-bold focus:outline-none"
            onChange={(e) => handleSelectCompetitionYearChange(e.target.value)}
            value={teamParams.year}
          >
            {teamCompetitionYears.map((year, index) => {
              return (
                <option key={index} value={year}>
                  {year}
                </option>
              );
            })}
          </select>
        )}
      </div>
      {calendarInfoCards.length > 0 && (
        <div className="lg-mb-20 mb-[50px]">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {calendarInfoCards.map(
              (
                {
                  isoDate,
                  title,
                  team1,
                  team2,
                  callToActionUrl,
                  quarterScores,
                  dateLabelType,
                  place,
                  gsId,
                  preparation,
                  phase,
                },
                index,
              ) => (
                <Card
                  key={index}
                  isoDate={isoDate}
                  dateLabelType={dateLabelType}
                  title={title}
                  type="calendar"
                  callToActionUrl={callToActionUrl}
                  gsId={gsId}
                  live={
                    gsId && currentLives.length > 0 && currentLives.includes(gsId.toString())
                      ? true
                      : false
                  }
                  team1={team1}
                  team2={team2}
                  preparation={preparation}
                  phase={phase}
                  quarterScores={quarterScores}
                  place={place}
                  imageSizes="(min-width: 66em) 25vw, (min-width: 44em) 50vw, 100vw"
                ></Card>
              ),
            )}
          </div>
        </div>
      )}
    </div>
  );
}
